import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  storageTransfersApi,
  type CreateTransferPayload,
  type CreateTransferReasonPayload,
  type ListTransfersParams,
  type PreviewTransferPayload,
  type TransferDestinationsParams,
  type UpdateTransferMovePayload,
  type UpdateTransferPayload,
  type UpdateTransferReasonPayload,
} from '@/api/storageTransfersApi'
import { useTenant } from '@/providers/TenantProvider'
import { storageApi } from '@/api/storageApi'
import type { StorageCondition } from '@/types/storage'

/**
 * Storage-transfer planning queries and mutations.
 *
 * `selectedTenantId` is in every query key so switching tenant re-fetches
 * rather than showing another tenant's plans, per the repo's tenancy rule.
 */

const KEY = 'storage-transfers'
const REASONS_KEY = 'storage-transfer-reasons'
/** Capacity and the tree change when a plan freezes a unit, so both are stale. */
const STORAGE_KEY = 'storage-nodes'

export function useStorageTransfers(params: ListTransfersParams = {}) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'list', tenantId, params],
    enabled: !!tenantId,
    queryFn: () => storageTransfersApi.list(tenantId as string, params),
  })
}

export function useStorageTransfer(id: string | null | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'detail', tenantId, id],
    enabled: !!tenantId && !!id,
    queryFn: () => storageTransfersApi.get(tenantId as string, id as string),
  })
}

/**
 * Replacement units for this source.
 *
 * `enabled` on the id so the list is only computed when the wizard is open —
 * it reads every unit's capacity and the source's occupants, which is not work
 * to do for a page that merely renders a freezer.
 */
export function useTransferCandidates(sourceRootId: string | null | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'candidates', tenantId, sourceRootId],
    enabled: !!tenantId && !!sourceRootId,
    queryFn: () => storageTransfersApi.candidates(tenantId as string, sourceRootId as string),
  })
}

/**
 * The computed mapping for a source/target pair.
 *
 * A QUERY despite the endpoint being a POST: it persists nothing, and the
 * wizard steps back and forth between the pair and the move table, which
 * should not recompute a thousand-slot mirror each time.
 */
export function useTransferPreview(payload: PreviewTransferPayload | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [KEY, 'preview', tenantId, payload?.sourceRootId, payload?.targetRootId],
    enabled: !!tenantId && !!payload?.sourceRootId && !!payload?.targetRootId,
    queryFn: () => storageTransfersApi.preview(tenantId as string, payload as PreviewTransferPayload),
  })
}

/**
 * The Moves step's location picker (z8nrz7f2gh): free, compatible positions in
 * one unit for one occupant, searched server-side.
 */
export function useTransferDestinations(params: TransferDestinationsParams | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [
      KEY,
      'destinations',
      tenantId,
      params?.unitId,
      params?.lotId ?? null,
      params?.productId ?? null,
      params?.q ?? '',
    ],
    enabled: !!tenantId && !!params?.unitId,
    queryFn: () => storageTransfersApi.destinations(tenantId as string, params as TransferDestinationsParams),
    placeholderData: (prev) => prev,
  })
}

/** Other units an occupant could go to, at its condition, for the picker's unit list. */
export function useCompatibleUnits(requires: StorageCondition | null, enabled: boolean) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [STORAGE_KEY, 'free-space', tenantId, requires],
    enabled: enabled && !!tenantId,
    queryFn: () => storageApi.freeSpace(tenantId as string, requires),
  })
}

export function useTransferReasons() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [REASONS_KEY, tenantId],
    enabled: !!tenantId,
    queryFn: () => storageTransfersApi.listReasons(tenantId as string),
  })
}

/**
 * Invalidate every transfer view, and storage itself — a plan freezes slots —
 * and the task lists: approving a plan opens its task, cancelling drops it.
 */
function useInvalidateTransfers() {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: [KEY] })
    void client.invalidateQueries({ queryKey: [STORAGE_KEY] })
    void client.invalidateQueries({ queryKey: ['maintenance-events'] })
  }
}

export function useCreateTransfer() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useInvalidateTransfers()
  return useMutation({
    mutationFn: (payload: CreateTransferPayload) =>
      storageTransfersApi.create(tenantId as string, payload),
    onSuccess: invalidate,
  })
}

export function useUpdateTransfer(id: string) {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useInvalidateTransfers()
  return useMutation({
    mutationFn: (payload: UpdateTransferPayload) =>
      storageTransfersApi.update(tenantId as string, id, payload),
    onSuccess: invalidate,
  })
}

export function useUpdateTransferMove(id: string) {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useInvalidateTransfers()
  return useMutation({
    mutationFn: ({ moveId, ...payload }: UpdateTransferMovePayload & { moveId: string }) =>
      storageTransfersApi.updateMove(tenantId as string, id, moveId, payload),
    onSuccess: invalidate,
  })
}

/**
 * Approve a plan.
 *
 * The id is a MUTATION argument, not a hook argument — the way every mutation
 * in `useStorage.ts` already takes it. Taking it at hook-creation time is what
 * broke this: the wizard creates the draft and approves it in one handler, so
 * the mutation closed over the `created` state from the render BEFORE
 * `setCreated` applied, sent an empty id, and the request came out as
 * `/inventory/storage-transfers//ready` — a 404 naming nothing, with a real
 * DRAFT left behind holding the one-live-transfer-per-source index.
 *
 * `revalidate`, `planReturn` and `cancel` below still take theirs at hook
 * level. That is safe only because their one caller is the detail page, whose
 * id comes from the URL and is therefore never a render behind — and it is
 * left alone rather than "fixed while here". If a second caller ever creates
 * and acts in one handler, move it to a mutation argument too.
 */
export function useReadyTransfer() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useInvalidateTransfers()
  return useMutation({
    mutationFn: (id: string) => storageTransfersApi.ready(tenantId as string, id),
    onSuccess: invalidate,
  })
}

export function useRevalidateTransfer(id: string) {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useInvalidateTransfers()
  return useMutation({
    mutationFn: () => storageTransfersApi.revalidate(tenantId as string, id),
    onSuccess: invalidate,
  })
}

export function usePlanReturn(id: string) {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useInvalidateTransfers()
  return useMutation({
    mutationFn: () => storageTransfersApi.planReturn(tenantId as string, id),
    onSuccess: invalidate,
  })
}

export function useCancelTransfer(id: string) {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useInvalidateTransfers()
  return useMutation({
    mutationFn: ({ reason, signingToken }: { reason: string; signingToken: string }) =>
      storageTransfersApi.cancel(tenantId as string, id, reason, signingToken),
    onSuccess: invalidate,
  })
}

function useInvalidateReasons() {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: [REASONS_KEY] })
  }
}

export function useCreateTransferReason() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useInvalidateReasons()
  return useMutation({
    mutationFn: (payload: CreateTransferReasonPayload) =>
      storageTransfersApi.createReason(tenantId as string, payload),
    onSuccess: invalidate,
  })
}

export function useUpdateTransferReason() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useInvalidateReasons()
  return useMutation({
    mutationFn: ({ id, ...payload }: UpdateTransferReasonPayload & { id: string }) =>
      storageTransfersApi.updateReason(tenantId as string, id, payload),
    onSuccess: invalidate,
  })
}

export function useDeleteTransferReason() {
  const { selectedTenantId: tenantId } = useTenant()
  const invalidate = useInvalidateReasons()
  return useMutation({
    mutationFn: (id: string) => storageTransfersApi.deleteReason(tenantId as string, id),
    onSuccess: invalidate,
  })
}
